import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Lock, Mail, ArrowRight, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { formatError } from "@/lib/api";

export default function AdminLogin() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (user) navigate("/admin", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await login(email.trim(), password);
      toast.success("Welcome back!");
      navigate("/admin", { replace: true });
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#FDFBF7]" data-testid="admin-login-root">
      {/* Brand panel */}
      <div className="relative hidden lg:flex flex-col justify-between p-12 bg-[#0B1736] text-white overflow-hidden">
        <div className="absolute inset-0 grain opacity-30" />
        <Link to="/" className="relative flex items-center gap-2 group">
          <div className="w-10 h-10 rounded-xl bg-teal-500 grid place-items-center font-bold">S</div>
          <div>
            <div className="font-extrabold text-lg">Sunbright</div>
            <div className="text-xs text-slate-300 uppercase tracking-wider">Pharma Academy</div>
          </div>
        </Link>
        <div className="relative">
          <div className="kicker !text-teal-300">Staff Portal</div>
          <h2 className="mt-4 text-4xl font-bold leading-tight">Manage your academy<br />in real time.</h2>
          <p className="mt-4 text-slate-300 max-w-md">Edit content, toggle sections, manage enquiries — all from a single, secure dashboard.</p>
        </div>
        <div className="relative text-xs text-slate-400">© Sunbright Pharma Academy</div>
      </div>

      {/* Form */}
      <div className="grid place-items-center p-6 md:p-12">
        <form onSubmit={submit} className="w-full max-w-md" data-testid="admin-login-form">
          <Link to="/" className="text-xs font-semibold text-slate-400 hover:text-[#1E3A8A] transition">← Back to website</Link>
          <h1 className="mt-4 text-3xl font-bold text-[#0F172A]">Staff Login</h1>
          <p className="mt-2 text-sm text-slate-500">Sign in to manage your academy website.</p>

          <div className="mt-8 space-y-4">
            <div>
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Email</label>
              <div className="relative mt-1.5">
                <Mail className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="admin-email-input"
                  className="w-full rounded-xl border border-slate-200 pl-11 pr-4 py-3 text-sm focus:border-[#1E3A8A] focus:outline-none focus:ring-2 focus:ring-[#1E3A8A]/15"
                  placeholder="you@academy.com"
                />
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Password</label>
              <div className="relative mt-1.5">
                <Lock className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type={show ? "text" : "password"}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="admin-password-input"
                  className="w-full rounded-xl border border-slate-200 pl-11 pr-11 py-3 text-sm focus:border-[#1E3A8A] focus:outline-none focus:ring-2 focus:ring-[#1E3A8A]/15"
                  placeholder="••••••••"
                />
                <button type="button" onClick={() => setShow((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700" aria-label="toggle password">
                  {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <button type="submit" disabled={submitting} data-testid="admin-login-submit" className="btn-primary w-full disabled:opacity-60">
              {submitting ? "Signing in..." : <>Sign In <ArrowRight className="w-4 h-4" /></>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
